import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { format, isValid, parseISO } from 'date-fns'

const identity = (v) => v

// Defaults may be passed as a thunk so they are evaluated per write rather than
// captured once. dateCodec's "today" needs this: a dashboard left open overnight
// would otherwise keep comparing against the day it was mounted.
const unwrap = (v) => (typeof v === 'function' ? v() : v)

// Leaves list separators readable, matching the convention in util.js. Uses
// split/join because replaceAll is ES2021 and CRA will not polyfill a runtime
// method for our browserslist.
const toSearchString = (params) => {
    const s = params.toString().split('%2C').join(',')
    return s ? `?${s}` : ''
}

/* ------------------------------ codecs ------------------------------ */
// parse() returns undefined to mean "reject this value, fall back to the default".

export const stringCodec = { parse: identity, serialize: String }

export const boolCodec = {
    parse: (raw) => (raw === 'true' ? true : raw === 'false' ? false : undefined),
    serialize: (v) => (v ? 'true' : 'false'),
}

export const intCodec = {
    parse: (raw) => {
        if (!/^-?\d+$/.test(raw)) return undefined
        return Number(raw)
    },
    serialize: String,
}

// For opaque ids and codes only. Never use this for free text: a value
// containing a comma would silently split into two entries.
export const csvCodec = (item = stringCodec) => ({
    parse: (raw) =>
        raw
            .split(',')
            .filter(Boolean)
            .map(item.parse)
            .filter((v) => v !== undefined),
    serialize: (v) => (Array.isArray(v) ? v.map(item.serialize).join(',') : ''),
})

// Validates untrusted URL input against a whitelist. `allowed` may be a thunk
// for lists only known at render time, e.g. permission-gated tabs.
export const oneOfCodec = (allowed) => ({
    parse: (raw) => (unwrap(allowed).includes(raw) ? raw : undefined),
    serialize: String,
})

export const dateCodec = {
    parse: (raw) => {
        const d = parseISO(raw)
        return isValid(d) ? d : undefined
    },
    serialize: (d) => (d instanceof Date && isValid(d) ? format(d, 'yyyy-MM-dd') : ''),
}

/* ------------------------ imperative helpers ------------------------ */
// For params that are not React state. Both read-modify-write against the live
// history.location, so they only touch the key they are given and leave the rest
// of the query string intact.

export const setUrlParam = (history, key, value) => {
    const params = new URLSearchParams(history.location.search)
    if (value === null || value === undefined || value === '') params.delete(key)
    else params.set(key, String(value))
    const search = toSearchString(params)
    if (search === history.location.search) return
    history.replace({ pathname: history.location.pathname, search, hash: history.location.hash })
}

export const removeUrlParams = (history, ...keys) => {
    const params = new URLSearchParams(history.location.search)
    if (!keys.some((k) => params.has(k))) return
    keys.forEach((k) => params.delete(k))
    history.replace({
        pathname: history.location.pathname,
        search: toSearchString(params),
        hash: history.location.hash,
    })
}

/* -------------------------------- hook ------------------------------ */

/**
 * Drop-in useState replacement that mirrors one query param into the URL, so a
 * filtered view can be shared and survives a refresh.
 *
 *     const [tab, setTab] = useUrlState('tab', 'overview', oneOfCodec(tabList))
 *     const [search, setSearch] = useUrlState('search', '', { debounce: 700 })
 *
 * Why writes read history.location rather than useLocation(): history@4 updates
 * history.location synchronously inside setState() before notifying listeners,
 * while React batches the Router's re-render until after the effect flush. So
 * useLocation() is one commit stale, and rebuilding a query string from it drops
 * whatever another hook wrote in the same commit. Reading live also preserves
 * foreign params (?id=, ?surveyId=) for free.
 *
 * Writes to distinct keys are commutative, so several of these compose correctly
 * regardless of effect ordering -- which matters because effects run
 * child-before-parent and hooks for one screen may live in different components.
 *
 * @param {string} key                 query param name
 * @param {*} defaultValue             value (or thunk) meaning "param absent"
 * @param {object} [options]
 * @param {function} [options.parse]       raw string -> value, undefined to reject
 * @param {function} [options.serialize]   value -> raw string
 * @param {function} [options.isDefault]   override for dynamic defaults
 * @param {number}   [options.debounce=0]  ms to wait before writing
 * @param {boolean}  [options.enabled=true] false suppresses writes entirely
 */
export const useUrlState = (key, defaultValue, options = {}) => {
    const history = useHistory()
    const { parse = identity, serialize = String, isDefault, debounce = 0, enabled = true } = options

    // Refreshed every render so the write effect always sees current predicates
    // without needing them in its deps. Required where the default is dynamic:
    // on ClassroomHome the display mode decides which emotion list counts as
    // default, and a [value]-only effect would capture a stale isDefault and
    // persist or delete the wrong param.
    const optionsRef = useRef(null)
    optionsRef.current = { serialize, isDefault, defaultValue, enabled }

    const [value, setValue] = useState(() => {
        const fallback = unwrap(defaultValue)
        const raw = new URLSearchParams(history.location.search).get(key)
        if (raw === null) return fallback
        try {
            const parsed = parse(raw)
            return parsed === undefined ? fallback : parsed
        } catch {
            return fallback // a hand-typed URL must never crash a screen
        }
    })

    const write = useCallback(
        (next, scheduledPathname) => {
            const opts = optionsRef.current
            if (!opts.enabled) return
            // A debounced write queued on another screen must not land here.
            if (scheduledPathname && history.location.pathname !== scheduledPathname) return

            const params = new URLSearchParams(history.location.search)
            const serialized = opts.serialize(next)
            const atDefault = opts.isDefault ? opts.isDefault(next) : serialized === opts.serialize(unwrap(opts.defaultValue))

            if (atDefault || serialized === '') params.delete(key)
            else params.set(key, serialized)

            const search = toSearchString(params)
            // Idempotent: also keeps the hook correct if the mount guard is ever
            // defeated by a React 18 + StrictMode upgrade.
            if (search === history.location.search) return
            history.replace({ pathname: history.location.pathname, search, hash: history.location.hash })
        },
        [history, key]
    )

    const didMountRef = useRef(false)
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true
            return // never clobber the URL we were opened with
        }
        if (!debounce) {
            write(value)
            return
        }
        const scheduledPathname = history.location.pathname
        const t = setTimeout(() => write(value, scheduledPathname), debounce)
        // Cancelling on unmount drops the last keystroke, which is correct: the
        // URL is about to change anyway, and a late replace would write onto
        // whatever screen came next.
        return () => clearTimeout(t)
    }, [value, debounce, write, history])

    return [value, setValue]
}
